'use client';

import { c as _c } from "react/compiler-runtime";
import { Button } from '@repo/design-system/components/ds/button';
import { FlexRow } from '@repo/design-system/components/layout/flex-row';
import { Type } from '@repo/design-system/components/ui/type';
import { useHostMode, useHostType } from '@repo/host/hooks/use-host-info';
import { css } from '@repo/panda/css';
import type { SpacingToken, SystemStyleObject } from '@repo/panda/types';
import { HostMode, HostType } from '@repo/types/host';
import { getJsonCookieRecordValue, isTimestampWithinDays, setJsonCookieRecordTimestamp } from '@repo/utility/json-cookie-record';
import { type ReactNode, useCallback, useEffect, useState } from 'react';
const DISMISS_COOKIE_KEY = 'dismiss-open-in-wand-bar';
const DISMISS_DAYS = 7;
const MIN_WINDOW_WIDTH = 1200;
type OpenInWandBarProps = {
  dismissKey: string;
  dismissLabel: string;
  dismissButtonProps?: Omit<React.ComponentProps<typeof Button>, 'children' | 'onClick'>;
  actionButton: ReactNode;
  actionGap?: SpacingToken;
  className?: SystemStyleObject;
  onDismiss?: () => void;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  enabled?: boolean;
  position?: 'fixed' | 'absolute';
} & ({
  ctaText: ReactNode;
  content?: never;
} | {
  content: ReactNode;
  ctaText?: never;
});
export function OpenInWandBar(t0) {
  const $ = _c(32);
  const {
    ctaText,
    dismissKey,
    dismissLabel,
    dismissButtonProps,
    actionButton,
    actionGap: t1,
    className,
    onDismiss,
    onMouseEnter,
    onMouseLeave,
    enabled: t2,
    position: t3,
    content
  } = t0;
  const actionGap = t1 === undefined ? "8" : t1;
  const enabled = t2 === undefined ? true : t2;
  const position = t3 === undefined ? "fixed" : t3;
  const hostType = useHostType();
  const hostMode = useHostMode();
  const [visible, setVisible] = useState(false);
  const [windowWidth, setWindowWidth] = useState(typeof window !== "undefined" ? window.innerWidth : 0);
  let t4;
  let t5;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => {
      if (typeof window === "undefined") {
        return;
      }
      const handleResize = () => setWindowWidth(window.innerWidth);
      window.addEventListener("resize", handleResize);
      return () => window.removeEventListener("resize", handleResize);
    };
    t5 = [];
    $[0] = t4;
    $[1] = t5;
  } else {
    t4 = $[0];
    t5 = $[1];
  }
  useEffect(t4, t5);
  let t6;
  let t7;
  if ($[2] !== dismissKey || $[3] !== enabled || $[4] !== hostMode || $[5] !== hostType || $[6] !== windowWidth) {
    t6 = () => {
      if (!enabled || hostType !== HostType.WEB || hostMode !== HostMode.WINDOW || windowWidth < MIN_WINDOW_WIDTH) {
        setVisible(false);
        return;
      }
      const dismissedAt = getJsonCookieRecordValue(DISMISS_COOKIE_KEY, dismissKey);
      if (isTimestampWithinDays(dismissedAt, DISMISS_DAYS)) {
        setVisible(false);
        return;
      }
      setVisible(true);
    };
    t7 = [enabled, hostType, hostMode, windowWidth, dismissKey];
    $[2] = dismissKey;
    $[3] = enabled;
    $[4] = hostMode;
    $[5] = hostType;
    $[6] = windowWidth;
    $[7] = t6;
    $[8] = t7;
  } else {
    t6 = $[7];
    t7 = $[8];
  }
  useEffect(t6, t7);
  let t8;
  if ($[9] !== dismissKey || $[10] !== onDismiss) {
    t8 = () => {
      setVisible(false);
      setJsonCookieRecordTimestamp(DISMISS_COOKIE_KEY, dismissKey, DISMISS_DAYS);
      onDismiss?.();
    };
    $[9] = dismissKey;
    $[10] = onDismiss;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  const handleDismiss = t8;
  if (!visible) {
    return null;
  }
  const t9 = position === "fixed" ? "fixed" : "absolute";
  let t10;
  if ($[12] !== className || $[13] !== t9) {
    t10 = css({
      position: t9,
      left: "[50%]",
      bottom: "16",
      transform: "translateX(-50%)",
      zIndex: 20,
      background: "blackAlpha.20",
      borderRadius: "16",
      boxShadow: "lg",
      display: "flex",
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",
      padding: "12",
      gap: "12",
      backdropFilter: "[blur(25px)]"
    }, className);
    $[12] = className;
    $[13] = t9;
    $[14] = t10;
  } else {
    t10 = $[14];
  }
  let t11;
  if ($[15] !== content || $[16] !== ctaText) {
    t11 = content ?? <FlexRow align="center" gap="8"><Type variant="body-s" color="white">{ctaText}</Type></FlexRow>;
    $[15] = content;
    $[16] = ctaText;
    $[17] = t11;
  } else {
    t11 = $[17];
  }
  let t12;
  if ($[18] !== dismissButtonProps || $[19] !== dismissLabel || $[20] !== handleDismiss) {
    t12 = <Button variant="filled" color="secondary" {...dismissButtonProps} onClick={handleDismiss}>{dismissLabel}</Button>;
    $[18] = dismissButtonProps;
    $[19] = dismissLabel;
    $[20] = handleDismiss;
    $[21] = t12;
  } else {
    t12 = $[21];
  }
  let t13;
  if ($[22] !== actionButton || $[23] !== actionGap || $[24] !== t12) {
    t13 = <FlexRow align="center" gap={actionGap}>{t12}{actionButton}</FlexRow>;
    $[22] = actionButton;
    $[23] = actionGap;
    $[24] = t12;
    $[25] = t13;
  } else {
    t13 = $[25];
  }
  let t14;
  if ($[26] !== onMouseEnter || $[27] !== onMouseLeave || $[28] !== t10 || $[29] !== t11 || $[30] !== t13) {
    t14 = <aside className={t10} onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave}>{t11}{t13}</aside>;
    $[26] = onMouseEnter;
    $[27] = onMouseLeave;
    $[28] = t10;
    $[29] = t11;
    $[30] = t13;
    $[31] = t14;
  } else {
    t14 = $[31];
  }
  return t14;
}